import React, { useEffect, useMemo, useState } from 'react'
import AccountTypeSelector from '../components/AccountTypeSelector'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

type Payment = {
  id: string
  date: string
  playerId: string
  playerName?: string
  tournamentId?: string
  tournamentName?: string
  amount: number
  accountId?: string
  account?: {
    id: string
    name: string
    type: string
    currency: string
  }
}

const fmt = (n: number) => (isFinite(n) ? n.toFixed(2) : '0.00')
const formatDateDDMMYYYY = (raw?: string) => {
  if (!raw) return ''
  const source = String(raw)
  if (source.length >= 10 && source[4] === '-' && source[7] === '-') {
    const yyyy = source.slice(0, 4)
    const mm = source.slice(5, 7)
    const dd = source.slice(8, 10)
    return `${dd}.${mm}.${yyyy}`
  }
  const d = new Date(source)
  if (Number.isNaN(d.getTime())) return source.slice(0, 10)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}.${mm}.${yyyy}`
}

const PlayerPayments: React.FC = () => {
  const [players, setPlayers] = useState<any[]>([])
  const [tournaments, setTournaments] = useState<any[]>([])
  const [list, setList] = useState<Payment[]>([])
  const [playerPaidTotals, setPlayerPaidTotals] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)

  const [playerId, setPlayerId] = useState('')
  const [tournamentId, setTournamentId] = useState('')
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<'CASH'|'CARD'>('CASH')
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0,10))
  const [accountId, setAccountId] = useState('')

  // Загрузка сохраненного фильтра турнира из localStorage
  useEffect(() => {
    const savedTournamentId = localStorage.getItem('player-payments-tournament-id')
    if (savedTournamentId !== null) {
      setTournamentId(savedTournamentId)
    }
  }, [])

  // Сохранение фильтра турнира в localStorage при изменении
  const handleTournamentChange = (value: string) => {
    setTournamentId(value)
    // Сохраняем любое значение, включая пустое ("Не указывать")
    localStorage.setItem('player-payments-tournament-id', value)
  }

  const total = useMemo(() => {
    let arr = list
    if (tournamentId) arr = arr.filter(x => x.tournamentId === tournamentId)
    if (playerId) arr = arr.filter(x => x.playerId === playerId)
    return arr.reduce((s, p) => s + Number(p.amount || 0), 0)
  }, [list, playerId, tournamentId])
  const [editId, setEditId] = useState<string | null>(null)
  const [editAmount, setEditAmount] = useState<string>('')
  const [editDate, setEditDate] = useState<string>('')
  const [editMethod, setEditMethod] = useState<'CASH'|'CARD'>('CASH')
  const [editAccountId, setEditAccountId] = useState('')
  const [editPlayerId, setEditPlayerId] = useState('')
  const [editTournamentId, setEditTournamentId] = useState('')
  
  // JSON modal states
  const [showJsonModal, setShowJsonModal] = useState(false)
  const [selectedPaymentJson, setSelectedPaymentJson] = useState<Payment | null>(null)
  const tournamentStatusMeta: Record<string, { order: number; label: string }> = {
    ACTIVE: { order: 0, label: 'Активные' },
    PLANNED: { order: 1, label: 'Запланированные' },
    COMPLETED: { order: 2, label: 'Завершенные' },
    CANCELLED: { order: 3, label: 'Отмененные' }
  }

  const loadRefs = async () => {
    try {
      const [p, t] = await Promise.all([
        apiGet<{ players?: any[] } | any[]>(`/players?limit=1000&_=${Date.now()}`),
        apiGet<{ tournaments?: any[] } | any[]>(`/tournaments?limit=1000&_=${Date.now()}`)
      ])
      setPlayers(Array.isArray(p?.players) ? p.players : (Array.isArray(p) ? p : []))
      setTournaments(Array.isArray(t?.tournaments) ? t.tournaments : (Array.isArray(t) ? t : []))
    } catch {}
  }

  const loadList = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.set('type', 'PAYMENT')
      params.set('page', '1')
      params.set('limit', playerId || tournamentId ? '5000' : '300')
      if (playerId) params.set('playerId', playerId)
      if (tournamentId) params.set('tournamentId', tournamentId)
      params.set('_', String(Date.now()))
      const data = await apiGet<{ items?: any[]; settlements?: any[] } | any[]>(`/settlements?${params.toString()}`)
      const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data?.settlements) ? data.settlements : (Array.isArray(data) ? data : []))
      // Фильтруем только записи с playerId (исключаем оплаты поставщикам)
      const playerPayments = items.filter((s: any) => s.playerId && !s.supplierId)
      const mapped: Payment[] = playerPayments.map((s: any) => ({
        id: s.id,
        date: s.createdAt,
        playerId: s.playerId,
        playerName: s.player?.name || players.find(p=>p.id===s.playerId)?.name,
        tournamentId: s.tournamentId,
        tournamentName: s.tournament?.name || tournaments.find(t=>t.id===s.tournamentId)?.name,
        amount: Number(s.amount || 0),
        accountId: s.accountId,
        account: s.account
      }))
      setList(mapped)
    } catch {
      setList([])
    } finally {
      setLoading(false)
    }
  }

  const loadPlayerPaidTotals = async (currentTournamentId?: string) => {
    try {
      const tournamentFilter = String(currentTournamentId ?? tournamentId ?? '').trim()
      const query = tournamentFilter ? `?tournamentId=${encodeURIComponent(tournamentFilter)}` : ''
      const data = await apiGet<{ items?: Array<{ playerId: string; totalAmount: number }> }>(
        `/player-payments/summary/by-player${query}`
      )
      const items = Array.isArray(data?.items) ? data.items : []
      const nextTotals: Record<string, number> = {}
      items.forEach((item) => {
        const id = String(item?.playerId || '').trim()
        if (!id) return
        nextTotals[id] = Number(item?.totalAmount || 0)
      })
      setPlayerPaidTotals(nextTotals)
    } catch (e) {
      console.error('Не удалось загрузить сводку оплат по игрокам:', e)
      setPlayerPaidTotals({})
    }
  }

  const submit = async () => {
    if (!playerId || !amount) return
    try {
      const body = { playerId, tournamentId: tournamentId || undefined, amount: Number(amount), date, method, accountId: accountId || undefined }
      await apiPost('/player-payments', body)
      setAmount('')
      setAccountId('')
      await loadList()
      await loadPlayerPaidTotals(tournamentId)
    } catch (e) {
      console.error(e)
      alert('Не удалось создать оплату')
    }
  }

  const openEdit = (p: Payment) => {
    setEditId(p.id)
    setEditAmount(String(p.amount))
    setEditDate((p.date || '').slice(0,10))
    setEditMethod('CASH')
    setEditAccountId(p.accountId || '')
    setEditPlayerId(p.playerId || '')
    setEditTournamentId(p.tournamentId || '')
  }

  const saveEdit = async () => {
    if (!editId) return
    try {
      const body: any = {}
      if (editAmount !== '') body.amount = Number(editAmount)
      if (editDate) body.date = editDate
      body.method = editMethod
      if (editAccountId) body.accountId = editAccountId
      if (editPlayerId) body.playerId = editPlayerId
      if (editTournamentId) body.tournamentId = editTournamentId
      await apiPut(`/player-payments/${editId}`, body)
      setEditId(null)
      await loadList()
      await loadPlayerPaidTotals(editTournamentId || tournamentId)
    } catch (e) {
      console.error(e)
      alert('Не удалось сохранить')
    }
  }

  const removePayment = async (id: string) => {
    if (!confirm('Удалить оплату?')) return
    try {
      await apiDelete(`/player-payments/${id}`)
      await loadList()
      await loadPlayerPaidTotals(tournamentId)
    } catch (e) {
      console.error(e)
      alert('Не удалось удалить')
    }
  }

  const handleShowJson = (payment: Payment) => {
    setSelectedPaymentJson(payment)
    setShowJsonModal(true)
  }

  const closeJsonModal = () => {
    setShowJsonModal(false)
    setSelectedPaymentJson(null)
  }

  useEffect(() => {
    loadRefs()
    loadPlayerPaidTotals('')
  }, [])

  useEffect(() => {
    loadList()
  }, [playerId, tournamentId, players, tournaments])

  useEffect(() => {
    loadPlayerPaidTotals(tournamentId)
  }, [tournamentId])

  // вычисляем зарегистрированных игроков по выбранному турниру
  const paidByPlayer = useMemo(() => {
    const acc = new Map<string, number>()
    Object.entries(playerPaidTotals).forEach(([playerId, total]) => {
      acc.set(playerId, Number(total || 0))
    })
    return acc
  }, [playerPaidTotals])

  const filteredPlayers = useMemo(() => {
    const basePlayers = (() => {
      if (!tournamentId) return players
      const t = tournaments.find(t=>t.id===tournamentId)
      const m = Array.isArray(t?.memberships) ? t.memberships : []
      const ids = new Set(m.map((x:any)=> x.playerId))
      return players.filter(p => ids.has(p.id))
    })()

    return [...basePlayers].sort((a:any,b:any)=> {
      const diff = (paidByPlayer.get(a.id) || 0) - (paidByPlayer.get(b.id) || 0)
      if (Math.abs(diff) > 0.0001) return diff
      return String(a.name||'').localeCompare(String(b.name||''), 'ru')
    })
  }, [players, tournamentId, tournaments, paidByPlayer])

  const tournamentsGrouped = useMemo(() => {
    const toDateTs = (value?: string): number => {
      if (!value) return Number.NEGATIVE_INFINITY
      const parsed = new Date(value).getTime()
      return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY
    }

    const groups = new Map<string, { label: string; order: number; items: any[] }>()
    for (const tournament of tournaments) {
      const statusKey = String(tournament?.status || '').trim().toUpperCase()
      const statusInfo = tournamentStatusMeta[statusKey] || { order: 99, label: 'Прочие' }
      if (!groups.has(statusInfo.label)) {
        groups.set(statusInfo.label, { label: statusInfo.label, order: statusInfo.order, items: [] })
      }
      groups.get(statusInfo.label)!.items.push(tournament)
    }

    const sortedGroups = Array.from(groups.values()).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, 'ru'))
    for (const group of sortedGroups) {
      group.items.sort((a, b) => {
        const aTs = Math.max(toDateTs(a?.endDate), toDateTs(a?.startDate))
        const bTs = Math.max(toDateTs(b?.endDate), toDateTs(b?.startDate))
        if (aTs !== bTs) return bTs - aTs
        return String(a?.name || '').localeCompare(String(b?.name || ''), 'ru')
      })
    }
    return sortedGroups
  }, [tournaments])

  // фильтрация и сортировка журнала по выбранным параметрам
  const filteredList = useMemo(() => {
    let arr = list
    if (tournamentId) arr = arr.filter(x => x.tournamentId === tournamentId)
    if (playerId) arr = arr.filter(x => x.playerId === playerId)
    // сортировка по дате в обратном порядке (свежие выше)
    return [...arr].sort((a,b) => String(b.date||'').localeCompare(String(a.date||'')))
  }, [list, playerId, tournamentId])

  return (
    <div className="space-y-4">
      <div className="border rounded p-3">
        <div className="font-semibold mb-3">Новая оплата игрока</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Игрок</label>
            <select className="border rounded px-2 py-1 text-sm w-full" value={playerId} onChange={e=>setPlayerId(e.target.value)}>
              <option value="">Выберите игрока</option>
              {filteredPlayers.map(p => (
                <option key={p.id} value={p.id}>{`${p.name} • ${Math.round(paidByPlayer.get(p.id) || 0)}`}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Турнир (опц.)</label>
            <select className="border rounded px-2 py-1 text-sm w-full" value={tournamentId} onChange={e=>handleTournamentChange(e.target.value)}>
              <option value="">Не указывать</option>
              {tournamentsGrouped.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.items.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Дата</label>
            <input type="date" className="border rounded px-2 py-1 text-sm w-full" value={date} onChange={e=>setDate(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <AccountTypeSelector
              value={accountId}
              onChange={(id) => {
                setAccountId(id)
                // Автоматически определяем метод оплаты на основе типа счета
                if (id) {
                  // Здесь нужно будет получить тип счета из выбранного аккаунта
                  // Пока оставляем как есть, метод будет определяться в backend
                }
              }}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Сумма, MDL</label>
            <input type="number" step="0.01" className="border rounded px-2 py-1 text-sm w-full" value={amount} onChange={e=>setAmount(e.target.value)} />
          </div>
          <div>
            <button className="px-3 py-1 text-sm border rounded bg-white hover:bg-gray-50 w-full" onClick={submit}>Создать</button>
          </div>
        </div>
      </div>

      <div className="overflow-auto">
        <table className="min-w-full bg-white border rounded">
          <thead>
            <tr className="bg-gray-50 text-left text-sm text-gray-600">
              <th className="px-3 py-2 border-b">Дата</th>
              <th className="px-3 py-2 border-b">Игрок</th>
              <th className="px-3 py-2 border-b">Турнир</th>
              <th className="px-3 py-2 border-b text-right">Сумма</th>
              <th className="px-3 py-2 border-b">Счет</th>
              <th className="px-3 py-2 border-b text-right">Действия</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading && (<tr><td className="px-3 py-3 text-gray-500" colSpan={6}>Загрузка...</td></tr>)}
            {!loading && filteredList.length === 0 && (<tr><td className="px-3 py-3 text-gray-500" colSpan={6}>Нет данных</td></tr>)}
            {!loading && filteredList.map(p => (
              <tr key={p.id}>
                <td className="px-3 py-2 border-b whitespace-nowrap">{formatDateDDMMYYYY(p.date)}</td>
                <td className="px-3 py-2 border-b">{p.playerName || p.playerId}</td>
                <td className="px-3 py-2 border-b">{p.tournamentName || '-'}</td>
                <td className="px-3 py-2 border-b text-right">{fmt(p.amount)}</td>
                <td className="px-3 py-2 border-b">{p.account ? `${p.account.name} (${p.account.currency})` : '-'}</td>
                <td className="px-3 py-2 border-b text-right whitespace-nowrap">
                  <div className="flex items-center justify-end gap-1">
                    <button 
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded" 
                      onClick={()=>openEdit(p)}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button 
                      className="p-1 text-red-600 hover:bg-red-50 rounded" 
                      onClick={()=>removePayment(p.id)}
                      title="Удалить"
                    >
                      🗑️
                    </button>
                    <button 
                      className="p-1 text-gray-600 hover:bg-gray-50 rounded" 
                      onClick={()=>handleShowJson(p)}
                      title="Просмотр JSON"
                    >
                      📄
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {list.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 text-sm font-medium">
                <td className="px-3 py-2 border-t" colSpan={4}>Итого</td>
                <td className="px-3 py-2 border-t text-right">{fmt(total)}</td>
                <td className="px-3 py-2 border-t"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {editId && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">Правка оплаты</div>
              <button className="px-3 py-1 border rounded" onClick={()=>setEditId(null)}>Закрыть</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Игрок</label>
                <select className="border rounded px-3 py-2 w-full" value={editPlayerId} onChange={e=>setEditPlayerId(e.target.value)}>
                  <option value="">Выберите игрока</option>
                  {players.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Турнир (опц.)</label>
                <select className="border rounded px-3 py-2 w-full" value={editTournamentId} onChange={e=>setEditTournamentId(e.target.value)}>
                  <option value="">Не указывать</option>
                  {tournamentsGrouped.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.items.map((t: any) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Дата</label>
                <input type="date" className="border rounded px-3 py-2 w-full" value={editDate} onChange={e=>setEditDate(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Счет</label>
                <AccountTypeSelector
                  value={editAccountId}
                  onChange={setEditAccountId}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Сумма, MDL</label>
                <input type="number" step="0.01" className="border rounded px-3 py-2 w-full" value={editAmount} onChange={e=>setEditAmount(e.target.value)} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="px-3 py-2 border rounded" onClick={()=>setEditId(null)}>Отмена</button>
              <button className="px-3 py-2 border rounded" onClick={saveEdit}>Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {/* JSON Modal */}
      {showJsonModal && selectedPaymentJson && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-2xl rounded shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">JSON документа</div>
              <button className="px-3 py-1 border rounded" onClick={closeJsonModal}>Закрыть</button>
            </div>
            <div className="bg-gray-100 p-3 rounded text-sm font-mono overflow-auto max-h-96">
              <pre>{JSON.stringify(selectedPaymentJson, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PlayerPayments


